import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  InputBase,
  Badge, 
  Select,
  MenuItem,
  FormControl,  
} from "@mui/material";
import {
  Menu,
  Chat,
  AutoAwesome,
  Settings,
  Logout,
} from "@mui/icons-material";
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { isTokenExpired } from 'hooks/useAuthCheck';
import { 
  setTimeMode,
  setLogout,
  setFlights,
  appendFlights,
  toggleChat,
  toggleAICopilot,
} from "state";
import { 
  NAV_HEIGHT, 
  AIRLINE_COUNTRY_CODE, 
  DASHBOARD_TIMER_INTERVAL,
} from 'state/constants';
import api from 'state/axios';
import { getFlagUrl } from 'state/helpers';
import { 
  chunkFlights,   
  updatePreferences,
} from 'state/api';
import { disconnectSocket } from "state/socket";
import { logClientAction } from 'state/logger';
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";
import DatePickerRange from "components/DatePickerRange";
import FlexBetween from "components/FlexBetween";   
import DropDownNotification from 'components/DropDownNotification';


const Navbar = ({ onMobileMenuOpen }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();  
  const token = useSelector((state) => state.token);
  const timeMode = useSelector((state) => state.timemode);
  const params = useSelector((state) => state.params);
  const user = useSelector((state) => state.user);
  const unreadCount = useSelector((state) => state.unreadCount);
  const userId = user._id || null;
  const email = user.email || null;
  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.toUpperCase();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { startDate, endDate } = useDateRangeContext();

  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const paramsMap = useMemo(
    () => new Map(params?.map(p => [p.param, p.value]) ?? []),
    [params]
  );
  
  const LOAD_TIMER_INTERVAL = Number(paramsMap.get("DASHBOARD_TIMER_INTERVAL")) || DASHBOARD_TIMER_INTERVAL;
  const LOAD_CHUNK_DAYS = Number(paramsMap.get("LOAD_CHUNK_DAYS")) || 31;
  const FETCH_TYPE = paramsMap.get("FETCH_TYPE") || "SEQUENCE";   
  const AIRLINE_NATIONAL_CODE = paramsMap.get("AIRLINE_COUNTRY_CODE") || "VN";   
  const CARRIER_NATIONAL_CODE = AIRLINE_NATIONAL_CODE || AIRLINE_COUNTRY_CODE;
  const flagUrl = timeMode === "UTC" 
    ? (getFlagUrl("un") || UnitedNationsFlag) 
    : (getFlagUrl(CARRIER_NATIONAL_CODE) || VietnamFlag);
    
  
  useEffect(() => {
    if (!startDate || !endDate) return;

    let isFetching = false;
    const controller = new AbortController();

    const handleFlights = async () => {
      if (isFetching) return;
      isFetching = true;

      try {
        setLoading(true);
        setProgress(0);

        let isFirstChunk = true;
        await chunkFlights(
          timeMode,
          startDate,
          endDate,
          LOAD_CHUNK_DAYS,
          FETCH_TYPE,
          {
            concurrency: 2,
            retries: 2,
            backoff: 1000,
            signal: controller.signal,
            onProgress: setProgress,
            onChunk: (chunk) => {
              if (isFirstChunk) {
                dispatch(setFlights({ flights: chunk }));
                isFirstChunk = false;
              } else {
                dispatch(appendFlights({ flights: chunk }));
              }
            },
          }
        );
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error(error?.message || "Failed to fetch flights!");
        }
      } finally {
        isFetching = false;
        setLoading(false);
      }
    };
   
    handleFlights();
    
    const intervalId = setInterval(handleFlights, LOAD_TIMER_INTERVAL);

    return () => {
      controller.abort();
      clearInterval(intervalId);
    };
  }, [
    dispatch,
    timeMode,
    startDate,
    endDate,
    LOAD_TIMER_INTERVAL,
    LOAD_CHUNK_DAYS,
    FETCH_TYPE,
  ]);

  const handleLogOut = useCallback(async () => {
    try {
      const url = '/auth/logout';
      await api.post(url, {}, { withCredentials: true }); 
      
      if (!isTokenExpired(token)) {
        logClientAction("logout", { email });
        disconnectSocket(userId);
      }       
    } catch (error) {
      console.error(error?.message || "Failed to log out!");
    } finally {
      dispatch(setLogout());
    }
  }, [
    token, 
    userId, 
    email, 
    dispatch,
  ]);
  
  const ensureValidSession = useCallback(() => {
    if (!token || isTokenExpired(token)) {
      toast.error("Session expired. Please log in again.");
      dispatch(setLogout());
      return false;
    }
    return true;
  }, [token, dispatch]);

  const handleToggleTimeMode = useCallback(async () => {    
    try {
      if (!ensureValidSession()) return;

      dispatch(setTimeMode());

      const newTimeMode = timeMode === "UTC" ? "LocalBase" : "UTC";
      const updatedPreferences = { timeMode: newTimeMode };

      await updatePreferences(userId, updatedPreferences);
    } catch (error) {
      if ([401, 403].includes(error.response?.status)) {
        toast.error("Session expired. Please log in again.");
        dispatch(setLogout());
      } else {
        toast.error("Failed to update preferences.");
      }
    }
  }, [
    userId, 
    timeMode, 
    dispatch, 
    ensureValidSession,
  ]);

  
  return (
    <FlexBetween 
      zIndex={99}      
      position="sticky"
      top={0}
      height={NAV_HEIGHT}
      padding="0rem 1rem" 
      backgroundColor={theme.palette.background.alt}
    >
      <FlexBetween
        gap={1}
        sx={{ "& .datepicker": { maxWidth: "6.6rem" } }}
      >    
        {isMobile && (
          <IconButton onClick={onMobileMenuOpen}>
            <Menu />
          </IconButton>
        )}

        <DatePickerRange fullWidth />  
      </FlexBetween>

      <FlexBetween gap={1}>
        {loading ? (
          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} />
            {progress > 0 && (
              <Typography
                variant="caption"
                component="div"
                sx={{
                  position: "absolute",
                  fontSize: "0.6rem",
                }}
              >
                {progress}%
              </Typography>
            )}
          </Box>
        ) : (
          <Box 
            display="flex" 
            alignItems="center"
            onClick={handleToggleTimeMode}
            sx={{ p: 0.5 }}            
          >
            <img
              alt="Flag"
              width={25}
              height={25}                
              src={flagUrl}
              style={{ 
                cursor: "pointer", 
                objectFit: "cover", 
                borderRadius: "50%",
                transition: "opacity 0.3s",
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
            />
          </Box>
        )}
        
        <DropDownNotification />
       
        <IconButton
          onClick={() => dispatch(toggleAICopilot())}
          sx={{ p: 0.5, display: 'none' }}
        >
          <AutoAwesome sx={{ color: theme.palette.primary.main }} />
        </IconButton>

        <IconButton
          onClick={() => dispatch(toggleChat())}
          sx={{ p: 0.5 }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <Chat sx={{ color: theme.palette.text.primary }} />
          </Badge>
        </IconButton>

        {!isMobile && (                
          <FormControl variant="standard"
            sx={{
              border: "1px solid",
              borderColor: theme.palette.neutral.main,
              borderRadius: 1,
            }}
          >
            <Select
              value={fullName}
              sx={{
                width: 222,
                p: "0.2rem 0.5rem",
                borderRadius: 1,
              }}
              input={<InputBase />}
            >
              <MenuItem value={fullName}>
                <Typography>{fullName.substring(0, 30)}</Typography>
              </MenuItem>
              <MenuItem onClick={() => navigate('/settings')} sx={{ gap: 1 }}>
                <Settings fontSize="small" /> Settings
              </MenuItem>
              <MenuItem onClick={handleLogOut} sx={{ gap: 1 }}>
                <Logout fontSize="small" /> Log Out
              </MenuItem>
            </Select>
          </FormControl>        
        )}
      </FlexBetween>

    </FlexBetween>
  );
};

export default Navbar;
